/**
 * <RevisionProgramRow> — un programme de révision, sur une ligne (molecule).
 *
 * La forme sobre de `<RevisionProgramCard>` : le picto de la matière, le titre du programme, la
 * matière et sa durée en dessous, un chevron. Toute la ligne ouvre le programme. Un état court
 * peut suivre (`badge`) : « Adaptable jusqu'à la veille » sur le programme d'un stage acheté.
 *
 * **Elle ne dessine pas de carte** : le bloc « Suivi » est UNE carte-liste, et ses lignes
 * s'alignent grâce à `<CardListRow>`. La carte reste ce qu'elle est ailleurs (page Programme de
 * révision), où un programme est l'objet de la page et non une ligne de synthèse.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../CardList"
import { type UIRevisionProgram } from "../../../ports/primary/ui/types"

export type RevisionProgramRowProps = {
    program: UIRevisionProgram
    /** Ouvre le programme — toute la ligne est la cible. */
    onPress?: () => void
    testID?: string
}

export function RevisionProgramRow(props: RevisionProgramRowProps) {
    const { program, onPress, testID } = props
    const { title, subject, durationHours, badge } = program

    // Même formule que la carte : « Mathématiques » ou « Mathématiques · 1h ».
    const meta = durationHours != null ? `${subject.name} · ${durationHours}h` : subject.name

    return (
        <Pressable testID={testID} onPress={onPress} disabled={onPress == null} accessibilityRole="button">
            <CardListRow>
                <Row gap="3" align="center">
                    <ImageIcon uri={subject.iconUrl} size={28} />

                    <Stack gap="1" style={styles.body}>
                        <Text size="text-md" weight="bold" numberOfLines={1}>
                            {title}
                        </Text>
                        <Text size="text-sm" color="text-muted" numberOfLines={1}>
                            {meta}
                        </Text>
                        {badge != null && (
                            <Row>
                                <Badge text={badge} variant="neutral" iconName="magic" />
                            </Row>
                        )}
                    </Stack>

                    {onPress != null && <Icon name="chevron-right" size={16} color="text-muted" />}
                </Row>
            </CardListRow>
        </Pressable>
    )
}

const styles = StyleSheet.create(() => ({
    body: {
        flex: 1,
        flexShrink: 1,
    },
}))
